import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import {
  IncentiveOffer,
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { getEligibleIncentiveOfferUnitTypeLabels } from 'modules/incentivesMarket/utils/calculateSellerSellLimits';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import incentiveOfferStatus from '../../../constants/incentiveOfferStatus';
import IncentiveOfferPartialityIcon from './IncentiveOfferPartialityIcon';
import IncentiveOfferStatusBadge, {
  IncentiveOfferStatusBadgeVariant,
} from '../../../components/IncentiveOfferStatusBadge';
import { getOfferStatus } from '../../../utils/getOfferStatus';

import './IncentiveOfferRow.scss';

type IncentiveOfferRowProps = {
  offer: IncentiveOffer;
  onViewDetail: (offerId: string) => void; // eslint-disable-line no-unused-vars
  showPrices?: boolean;
};

const rowBorderColor = {
  [IncentiveOfferType.SELL]: 'border-left-2 border-left-info',
  [IncentiveOfferType.BUY]: 'border-left-2 border-left-primary',
};

const IncentiveOfferRow: FC<IncentiveOfferRowProps> = ({
  offer,
  onViewDetail,
  showPrices = true,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === IncentiveOfferType.SELL;

  const partnerName = useMemo(() => {
    if (offer.isSellerNameHidden && offer.type === IncentiveOfferType.SELL)
      return t('HiddenName');
    if (typeof offer.beneficiary === 'string') {
      return '';
    }
    return (offer.beneficiary as IncentivesMarketBeneficiary)?.name || '';
  }, [offer.beneficiary, offer.isSellerNameHidden, offer.type, t]);

  const unitTypeLabels = useMemo(
    () =>
      getEligibleIncentiveOfferUnitTypeLabels(offer.sellerParticipationLimits),
    [offer.sellerParticipationLimits]
  );

  const totalAmount = offer.unitsCount.total * offer.unitPrice;

  const progressPercent = useMemo(() => {
    if (offer.unitsCount.total === 0) return 0;
    return Math.round((offer.unitsCount.agreed / offer.unitsCount.total) * 100);
  }, [offer.unitsCount]);

  const status = getOfferStatus(offer);
  const statusConfig = incentiveOfferStatus[status];

  const isPartialOffer = Boolean(offer?.isPartialAmountEnabled);

  const handleClick = () => {
    onViewDetail(offer._id);
  };

  return (
    <div
      className={`incentive-offer-row hover-shadow hover-scale ${
        rowBorderColor[offer.type]
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
    >
      <div
        className={`incentive-offer-row__icon ${
          status === 'ACTIVE' ? 'is-running' : ''
        }`}
        title={isSellOffer ? t('Sale') : t('Purchase')}
      >
        <em
          className={`icon ni ${
            isSellOffer ? 'ni-wallet-out' : 'ni-wallet-in'
          }`}
        />
      </div>

      <div className="incentive-offer-row__units text-start">
        {showPrices && (
          <div className="incentive-offer-row__units-name text-dark">
            {formatNumber(offer.unitsCount.total)}{' '}
            {t('IncentivesMarketUnits').toLowerCase()}
            <IncentiveOfferPartialityIcon
              id={offer._id}
              isPartial={isPartialOffer}
            />
          </div>
        )}

        {unitTypeLabels.length > 0 && (
          <div className="incentive-offer-row__units-desc text-soft">
            {unitTypeLabels.join(', ')}
          </div>
        )}
      </div>

      {showPrices && (
        <div className="incentive-offer-row__prices text-start">
          <div className="incentive-offer-row__prices-line">
            {t('IncentivesMarketPricePerUnit')}:{' '}
            <span className="amount text-dark">
              {formatCurrencyDecimals(offer.unitPrice)}
            </span>
          </div>
          <div className="incentive-offer-row__prices-line text-soft">
            {t('Total')}:{' '}
            <span className="amount text-dark">
              {formatCurrencyDecimals(totalAmount)}
            </span>
          </div>
        </div>
      )}

      <div className="incentive-offer-row__footer">
        <div className="incentive-offer-row__dates">
          <div className="incentive-offer-row__date-start incentive-offer-row__field">
            <span className="incentive-offer-row__field-label text-soft">
              {t('Start')}
            </span>
            <span className="incentive-offer-row__field-value incentive-offer-row__field-value--date text-dark">
              {format(new Date(offer.startDate), dateFormat)}
            </span>
          </div>
          <div className="incentive-offer-row__date-end incentive-offer-row__field">
            <span className="incentive-offer-row__field-label text-soft">
              {t('End')}
            </span>
            <span className="incentive-offer-row__field-value incentive-offer-row__field-value--date text-dark">
              {format(new Date(offer.endDate), dateFormat)}
            </span>
          </div>
        </div>

        <div className="incentive-offer-row__party incentive-offer-row__field text-start">
          <span className="incentive-offer-row__field-label text-soft">
            {isSellOffer ? t('Seller') : t('Buyer')}
          </span>
          <span className="incentive-offer-row__field-value text-dark">
            {partnerName}
          </span>
        </div>

        <div className="incentive-offer-row__status incentive-offer-row__field">
          <span className="incentive-offer-row__field-label text-soft">
            {t('Status')}
          </span>
          <IncentiveOfferStatusBadge
            status={status}
            variant={IncentiveOfferStatusBadgeVariant.TEXT}
          />
        </div>
      </div>

      <div className="incentive-offer-row__action">
        <Button
          className="btn-sm px-2"
          variant={ButtonVariants.PRIMARY}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          {t('Open')}
          <em className="icon ni ni-chevron-right ml-1" />
        </Button>
      </div>

      <div className="incentive-offer-row__progress">
        <div
          className={`progress-bar ${statusConfig.fill}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
};

export default IncentiveOfferRow;
